import type { ClassChapterOption, ClassNow, FrLevel } from "@tutor/protocol";
import { useEffect, useState } from "react";
import * as api from "./api";
import { Row } from "./controls";
import type { ParentStringKey, ParentTranslate } from "./strings";

// « En classe en ce moment » (ADR-0053): the child's classe, the chapter the class is on
// (from the fixed list of programme sections for that classe) and the next contrôle.
// The engine puts the chapter's skills first and reviews them before the date.

/** How far ahead the contrôle date picker goes; the API refuses later dates. */
const MAX_TEST_DAYS_AHEAD = 180;

const SUBJECT_KEYS: Readonly<Record<string, ParentStringKey>> = {
  math: "class.subject.math",
  fra: "class.subject.fra",
  sci: "class.subject.sci",
  svt: "class.subject.svt",
  pc: "class.subject.pc",
  hg: "class.subject.hg",
  emc: "class.subject.emc",
  tech: "class.subject.tech",
};

function isoDay(offsetDays: number, now = new Date()): string {
  return new Date(now.getTime() + offsetDays * 86_400_000).toISOString().slice(0, 10);
}

/** Chapters grouped by programme subject, in list order. */
function bySubject(chapters: readonly ClassChapterOption[]): [string, ClassChapterOption[]][] {
  const groups = new Map<string, ClassChapterOption[]>();
  for (const c of chapters) groups.set(c.subject, [...(groups.get(c.subject) ?? []), c]);
  return [...groups];
}

export function ClassNowGroup(props: {
  value: ClassNow | null;
  onChange: (next: ClassNow | null) => void;
  t: ParentTranslate;
  /** Builds element ids unique to the panel. */
  id: (name: string) => string;
}) {
  const { value, onChange, t, id } = props;
  const level = value?.level ?? null;
  const [lists, setLists] = useState<Partial<Record<FrLevel, readonly ClassChapterOption[]>>>({});
  const [failed, setFailed] = useState(false);
  const chapters = level === null ? undefined : lists[level];

  useEffect(() => {
    if (level === null || lists[level] !== undefined) return;
    let live = true;
    void api.getClassChapters(level).then((res) => {
      if (!live) return;
      if (!res.ok) {
        setFailed(true);
        return;
      }
      setFailed(false);
      setLists((prev) => ({ ...prev, [res.value.level]: res.value.chapters }));
    });
    return () => {
      live = false;
    };
  }, [level, lists]);

  const section = value?.section ?? null;
  const chosen = chapters?.find((c) => c.code === section);
  const subjectLabel = (code: string) => {
    const key = SUBJECT_KEYS[code];
    return key === undefined ? code : t(key);
  };

  return (
    <section className="pm-group" aria-labelledby={id("class")}>
      <h3 id={id("class")} className="pm-group__title">
        {t("class.group")}
      </h3>
      <Row
        label={t("class.level")}
        labelId={id("class-level")}
        hint={t("class.levelHint")}
        hintLabel={t("goals.info", { label: t("class.level") })}
      >
        <select
          className="pm-time"
          aria-labelledby={id("class-level")}
          value={level ?? ""}
          onChange={(e) => {
            const next = api.FR_LEVELS.find((l) => l === e.target.value);
            if (next === undefined || next === level) return;
            // Another classe has another list: the chapter and its date go.
            onChange({ level: next, section: null, testOn: null });
          }}
        >
          {level === null ? <option value="">{t("class.levelNone")}</option> : null}
          {api.FR_LEVELS.map((l) => (
            <option key={l} value={l}>
              {l}
            </option>
          ))}
        </select>
      </Row>
      {value === null ? null : (
        <div className="pm-row pm-row--block">
          <label htmlFor={id("class-section")} className="pm-row__label">
            {t("class.chapter")}
          </label>
          <select
            id={id("class-section")}
            className="pm-time pm-chapter"
            disabled={chapters === undefined}
            value={section ?? ""}
            onChange={(e) => {
              const code = e.target.value === "" ? null : e.target.value;
              onChange({
                ...value,
                section: code,
                testOn: code === null ? null : (value.testOn ?? null),
              });
            }}
          >
            <option value="">{t("class.chapterNone")}</option>
            {bySubject(chapters ?? []).map(([subject, list]) => (
              <optgroup key={subject} label={subjectLabel(subject)}>
                {list.map((c) => (
                  <option key={c.code} value={c.code}>
                    {c.topic}
                  </option>
                ))}
              </optgroup>
            ))}
          </select>
          {failed ? (
            <p className="pm-row__hint pm-row__hint--warn">{t("class.chaptersFailed")}</p>
          ) : null}
          {chosen !== undefined && value.chosenBy === "child" ? (
            <p className="pm-row__hint">{t("class.chosenByChild")}</p>
          ) : null}
        </div>
      )}
      {value === null || section === null ? null : (
        <Row
          label={t("class.test")}
          labelId={id("class-test")}
          hint={t("class.testHint")}
          hintLabel={t("goals.info", { label: t("class.test") })}
        >
          <span className="pm-range">
            <input
              type="date"
              className="pm-time"
              aria-labelledby={id("class-test")}
              min={isoDay(0)}
              max={isoDay(MAX_TEST_DAYS_AHEAD)}
              value={value.testOn ?? ""}
              onChange={(e) => {
                onChange({ ...value, testOn: e.target.value === "" ? null : e.target.value });
              }}
            />
            {value.testOn == null ? null : (
              <button
                type="button"
                className="parent-link"
                onClick={() => {
                  onChange({ ...value, testOn: null });
                }}
              >
                {t("class.testClear")}
              </button>
            )}
          </span>
        </Row>
      )}
    </section>
  );
}
